import { clearPrivanaLocalSessionState } from '@/auth/logout.js'
import { rememberLogoutContext } from '@/utils/returnTo.js'
import {
  clearStoredMemberClubContext,
  isCapacitorRuntime,
  markMemberLocallyLoggedOut,
  MEMBER_CALLBACK_INFLIGHT_KEY,
  MEMBER_PROFILE_KEY,
  MEMBER_THEME_KEY,
} from './lib/mobileBootstrap'
import { clearMemberDisplayCache, clearClubDisplayCache } from './lib/memberDisplayCache'
import { clearMemberResources } from './lib/resourceCache'
import { invalidateCanonicalIdentityResources } from './lib/canonicalIdentityResource'
import { invalidateMemberApiSession } from './api/memberPortalApiClient'
import { clearPendingClubLogin } from '@/utils/pendingClubLogin.js'

let mobileLogoutPromise = null
const LOCAL_AUTH_CLEAR_TIMEOUT_MS = 1500

async function clearLocalAuthSession(auth0Client) {
  let timeout
  try {
    await Promise.race([
      Promise.resolve().then(() => auth0Client.logout({ openUrl: false })).catch(() => undefined),
      new Promise((resolve) => { timeout = window.setTimeout(resolve, LOCAL_AUTH_CLEAR_TIMEOUT_MS) }),
    ])
  } finally {
    window.clearTimeout(timeout)
  }
}

function clearMemberDeviceState(clubCode, memberIdentity) {
  clearStoredMemberClubContext()
  markMemberLocallyLoggedOut()
  clearMemberDisplayCache({ clubCode, memberIdentity })
  clearClubDisplayCache(clubCode)
  // Account projections include subject, organisation and resolved member id;
  // clear the club namespace so no prior account variant survives logout.
  clearMemberResources({ clubCode })
  invalidateCanonicalIdentityResources()
  invalidateMemberApiSession()
  clearPendingClubLogin()
  try {
    ;[window.localStorage, window.sessionStorage].forEach((storage) => {
      storage?.removeItem(MEMBER_PROFILE_KEY)
      storage?.removeItem(MEMBER_THEME_KEY)
      storage?.removeItem(MEMBER_CALLBACK_INFLIGHT_KEY)
    })
  } catch { /* local completion is authoritative even when storage is unavailable */ }
}

export async function logoutMember(auth0Client, clubCode, orgId, options = {}) {
  if (isCapacitorRuntime()) {
    if (mobileLogoutPromise) return mobileLogoutPromise
    mobileLogoutPromise = (async () => {
      console.info('[members-logout]', { event: 'logout_requested', mode: 'local', externalBrowserUsed: false })
      clearMemberDeviceState(clubCode, options.memberIdentity)
      console.info('[members-logout]', { event: 'club_member_state_cleared' })
      // Auth0 SPA clears tokens/refresh credentials and its local auth cookie here,
      // but openUrl:false deliberately does not visit /v2/logout or clear hosted SSO.
      // The durable marker above therefore forces prompt=login on the next authorization.
      await clearLocalAuthSession(auth0Client)
      console.info('[members-logout]', { event: 'local_auth_cleared', externalBrowserUsed: false })
      options.setMemberOrgId?.(null)
      options.navigate?.('/app', { replace: true, state: { locallyLoggedOut: true } })
      console.info('[members-logout]', { event: 'navigation_reset_completed', externalBrowserUsed: false })
      return true
    })().finally(() => { mobileLogoutPromise = null })
    return mobileLogoutPromise
  }
  const base = window.location.origin

  clearStoredMemberClubContext()
  clearPrivanaLocalSessionState()
  if (clubCode) {
    rememberLogoutContext({ pathname: '/login' })
  }
  localStorage.removeItem('privana_member_profile')
  localStorage.removeItem('privana_member_theme')

  await auth0Client.logout({
    logoutParams: {
      returnTo: `${base}/login?logged_out=1`,
      organization: orgId || undefined,
    },
  })
}
